import { AxiosResponse } from 'axios';

import { ApiVersion } from '@/config';
import {
  IMasterNameOrganization,
  IMasterOrganizationRequest,
  IMasterVehicle,
  IMasterNamePerson,
  IMasterNameReq,
  IMasterVehicleForm,
  IProperty,
  IMasterIndexRelatedItems,
  IMasterAddress,
  IMasterAddressListParams,
  IMasterIndexLog,
  IWantedPerson,
  IMasterAddressPopulated,
} from '@/models';

import { http } from './http';
import {
  IGetParams,
  IIndeterminatePaginationResponse,
  IPaginatedListResponse,
} from './types';

export type MergeMasterPayload<
  Kind extends 'person' | 'organization' | 'vehicle' | 'address',
> = {
  targetId: string;
  mergedFields: Record<string, unknown>;
  /**
   * Top-level keys to clear on the target via `$unset`. Needed because
   * `JSON.stringify` drops keys whose value is `undefined`, so a chosen-empty
   * selected value can't be represented inside `mergedFields` alone.
   */
  unsetFields?: string[];
  /**
   * Note merge selection. `sourceNoteIds` lists matched-record notes the user
   * opted to bring over. `removeNoteIds` lists target-record notes the user
   * flagged for removal during the merge (soft-deleted server-side).
   */
  notes?: { sourceNoteIds: string[]; removeNoteIds?: string[] };
  /**
   * Per-flag merge selection for persons. `sourceFlagIds` brings source
   * MANUAL flags over to target. `removeFlagIds` soft-deletes target's
   * MANUAL flags the user dropped during the merge. Overlap (same `flag` ref
   * on both records) is hidden from the UI and resolved server-side.
   */
  flags?: Kind extends 'person'
    ? { sourceFlagIds: string[]; removeFlagIds?: string[] }
    : never;
  cautions?: Kind extends 'address'
    ? { sourceCautionIds: string[]; removeCautionIds?: string[] }
    : never;
  /**
   * Pre-plan resolution for the current user's agency (addresses only).
   *
   * `entry` is the resolved current-agency entry to write to the target
   * (`agencyId` and `prePlanOption` are filled server-side from the request
   * agency). `null` removes the target's current-agency entry. Omit the
   * whole `prePlans` field to leave the target's pre-plans untouched.
   *
   * Per the cross-agency policy, source's entries for other agencies are
   * not surfaced or copied; target's other-agency entries are preserved.
   */
  prePlans?: Kind extends 'address'
    ? { entry: Record<string, unknown> | null }
    : never;
  /**
   * Per-record opt-outs for reference reassignment. Ids in `skipIds` stay
   * pointing at the source (the about-to-be-deleted record), so they become
   * orphaned references after merge.
   */
  relatedRecords?: { skipIds: string[] };
};

export type MergeMasterResponse<TRecord> = {
  merged: TRecord;
  reassigned: Record<string, number>;
};

export enum MasterIndexApiPath {
  Addresses = 'addresses',
  Organizations = 'organizations',
  People = 'people',
  Properties = 'properties',
  Vehicles = 'vehicles',
  Logs = 'logs',
}

export const MasterIndexApi = {
  getMasterIndexLogs: (
    params: IGetParams,
  ): Promise<AxiosResponse<IPaginatedListResponse<IMasterIndexLog>>> => {
    return http.get(
      `api/${ApiVersion.V1}/master-index/${MasterIndexApiPath.Logs}`,
      { params },
    );
  },
  getMasterVehicles: (
    params?: IGetParams,
  ): Promise<AxiosResponse<IPaginatedListResponse<IMasterVehicle>>> => {
    return http.get(
      `api/${ApiVersion.V1}/master-index/${MasterIndexApiPath.Vehicles}`,
      { params },
    );
  },
  createMasterVehicle: (
    data: IMasterVehicleForm,
  ): Promise<AxiosResponse<IMasterVehicle>> => {
    return http.post(
      `api/${ApiVersion.V1}/master-index/${MasterIndexApiPath.Vehicles}`,
      data,
    );
  },
  updateMasterVehicle: ({
    _id,
    ...data
  }: IMasterVehicleForm): Promise<AxiosResponse<IMasterVehicle>> => {
    return http.put(
      `api/${ApiVersion.V1}/master-index/${MasterIndexApiPath.Vehicles}/${_id}`,
      data,
    );
  },
  deleteMasterVehicle: (id: string): Promise<AxiosResponse<null>> => {
    return http.delete(
      `api/${ApiVersion.V1}/master-index/${MasterIndexApiPath.Vehicles}/${id}`,
    );
  },
  getMasterVehicle: (id: string): Promise<AxiosResponse<IMasterVehicle>> => {
    return http.get(
      `api/${ApiVersion.V1}/master-index/${MasterIndexApiPath.Vehicles}/${id}`,
    );
  },
  getMasterPersons: (
    params?: IGetParams,
  ): Promise<AxiosResponse<IPaginatedListResponse<IMasterNamePerson>>> => {
    return http.get(
      `api/${ApiVersion.V1}/master-index/${MasterIndexApiPath.People}`,
      { params },
    );
  },
  getVerifiedMasterPersons: (
    params?: IGetParams,
  ): Promise<
    AxiosResponse<IIndeterminatePaginationResponse<IMasterNamePerson[]>>
  > => {
    return http.get(`api/${ApiVersion.V1}/inmates/people`, { params });
  },
  getMasterPerson: (id: string): Promise<AxiosResponse<IMasterNamePerson>> => {
    return http.get(
      `api/${ApiVersion.V1}/master-index/${MasterIndexApiPath.People}/${id}`,
    );
  },
  getPersonTabCounts: (
    id: string,
  ): Promise<
    AxiosResponse<{ relatedRecords: number; flags: number; notes: number }>
  > => {
    return http.get(
      `api/${ApiVersion.V1}/master-index/${MasterIndexApiPath.People}/tab-counts/${id}`,
    );
  },
  getMasterRelatedRecords: (
    type: MasterIndexApiPath,
    id: string,
    params?: IGetParams,
  ): Promise<
    AxiosResponse<IPaginatedListResponse<IMasterIndexRelatedItems[]>>
  > => {
    return http.get(
      `api/${ApiVersion.V1}/master-index/${type}/related-records/${id}`,
      { params },
    );
  },
  getMasterWantedRecords: (
    type: MasterIndexApiPath,
    id: string,
    params?: IGetParams,
  ): Promise<
    AxiosResponse<IIndeterminatePaginationResponse<IWantedPerson[]>>
  > => {
    return http.get(
      `api/${ApiVersion.V1}/master-index/${type}/wanted-records/${id}`,
      {
        params,
      },
    );
  },
  createMasterPerson: (
    data: IMasterNameReq,
  ): Promise<AxiosResponse<IMasterNamePerson>> => {
    return http.post(
      `api/${ApiVersion.V1}/master-index/${MasterIndexApiPath.People}`,
      data,
    );
  },
  updateMasterPerson: (
    data: IMasterNameReq,
  ): Promise<AxiosResponse<IMasterNamePerson>> => {
    return http.put(
      `api/${ApiVersion.V1}/master-index/${MasterIndexApiPath.People}/${data._id}`,
      data,
    );
  },
  deleteMasterPerson: (
    id: string,
  ): Promise<AxiosResponse<IMasterNamePerson>> => {
    return http.delete(
      `api/${ApiVersion.V1}/master-index/${MasterIndexApiPath.People}/${id}`,
    );
  },
  getMasterOrganizations: (
    params?: IGetParams,
  ): Promise<
    AxiosResponse<IPaginatedListResponse<IMasterNameOrganization>>
  > => {
    return http.get(
      `api/${ApiVersion.V1}/master-index/${MasterIndexApiPath.Organizations}`,
      { params },
    );
  },
  getMasterOrganization: (
    id: string,
  ): Promise<AxiosResponse<IMasterNameOrganization>> => {
    return http.get(
      `api/${ApiVersion.V1}/master-index/${MasterIndexApiPath.Organizations}/${id}`,
    );
  },
  getMasterOrganizationRelatedRecords: (
    id: string,
    params?: IGetParams,
  ): Promise<AxiosResponse<any>> => {
    return http.get(
      `api/${ApiVersion.V1}/master-index/${MasterIndexApiPath.Organizations}/related-records/${id}`,
      { params },
    );
  },
  createMasterOrganization: (
    data: IMasterOrganizationRequest,
  ): Promise<AxiosResponse<IMasterNameOrganization>> => {
    return http.post(
      `api/${ApiVersion.V1}/master-index/${MasterIndexApiPath.Organizations}`,
      data,
    );
  },
  updateMasterOrganization: (
    data: IMasterOrganizationRequest,
  ): Promise<AxiosResponse<IMasterNameOrganization>> => {
    return http.put(
      `api/${ApiVersion.V1}/master-index/${MasterIndexApiPath.Organizations}/${data._id}`,
      data,
    );
  },
  deleteMasterOrganization: (
    id: string,
  ): Promise<AxiosResponse<IMasterNameOrganization>> => {
    return http.delete(
      `api/${ApiVersion.V1}/master-index/${MasterIndexApiPath.Organizations}/${id}`,
    );
  },
  getProperty: async (id: string): Promise<AxiosResponse<IProperty>> => {
    return http.get(`api/${ApiVersion.V1}/master-index/properties/${id}`);
  },
  getPropertyRelatedRecords: async (
    id: string,
  ): Promise<AxiosResponse<IProperty>> => {
    return http.get(
      `api/${ApiVersion.V1}/master-index/properties/related-records/${id}`,
    );
  },
  getPropertyList: async (
    params: IGetParams,
  ): Promise<AxiosResponse<IPaginatedListResponse<IProperty>>> => {
    return http.get(`api/${ApiVersion.V1}/master-index/properties`, { params });
  },
  getMasterAddresses: (
    params?: IMasterAddressListParams,
  ): Promise<
    AxiosResponse<IIndeterminatePaginationResponse<IMasterAddress>>
  > => {
    return http.get(
      `api/${ApiVersion.V1}/master-index/${MasterIndexApiPath.Addresses}`,
      { params },
    );
  },

  getMasterAddress: (
    id: string,
  ): Promise<AxiosResponse<IMasterAddressPopulated>> => {
    return http.get(
      `api/${ApiVersion.V1}/master-index/${MasterIndexApiPath.Addresses}/${id}`,
    );
  },

  createMasterAddress: (
    data: Partial<IMasterAddress>,
  ): Promise<AxiosResponse<IMasterAddress>> => {
    return http.post(
      `api/${ApiVersion.V1}/master-index/${MasterIndexApiPath.Addresses}`,
      data,
    );
  },

  updateMasterAddress: (
    id: string,
    data: Partial<IMasterAddress>,
  ): Promise<AxiosResponse<IMasterAddress>> => {
    return http.put(
      `api/${ApiVersion.V1}/master-index/${MasterIndexApiPath.Addresses}/${id}`,
      data,
    );
  },

  deleteMasterAddress: (id: string): Promise<AxiosResponse<void>> => {
    return http.delete(
      `api/${ApiVersion.V1}/master-index/${MasterIndexApiPath.Addresses}/${id}`,
    );
  },

  getAddressRelatedRecords: (addressId: string, params?: IGetParams) => {
    return http.get(
      `api/${ApiVersion.V1}/master-index/addresses/related-records/${addressId}`,
      { params },
    );
  },

  getPersonRelatedRecordCounts: (
    id: string,
  ): Promise<
    AxiosResponse<{
      cfs: number;
      arrests: number;
      cases: number;
      warrants: number;
      tasks: number;
      dir: number;
    }>
  > => {
    return http.get(
      `api/${ApiVersion.V1}/master-index/${MasterIndexApiPath.People}/related-record-counts/${id}`,
    );
  },

  getPersonRelatedCfs: (
    id: string,
    params?: IGetParams,
  ): Promise<AxiosResponse<IPaginatedListResponse<any>>> => {
    return http.get(
      `api/${ApiVersion.V1}/master-index/${MasterIndexApiPath.People}/related-cfs/${id}`,
      { params },
    );
  },

  getPersonRelatedArrests: (
    id: string,
    params?: IGetParams,
  ): Promise<AxiosResponse<IPaginatedListResponse<any>>> => {
    return http.get(
      `api/${ApiVersion.V1}/master-index/${MasterIndexApiPath.People}/related-arrests/${id}`,
      { params },
    );
  },

  getPersonRelatedCases: (
    id: string,
    params?: IGetParams,
  ): Promise<AxiosResponse<IPaginatedListResponse<any>>> => {
    return http.get(
      `api/${ApiVersion.V1}/master-index/${MasterIndexApiPath.People}/related-cases/${id}`,
      { params },
    );
  },

  getPersonRelatedWarrants: (
    id: string,
    params?: IGetParams,
  ): Promise<AxiosResponse<IPaginatedListResponse<any>>> => {
    return http.get(
      `api/${ApiVersion.V1}/master-index/${MasterIndexApiPath.People}/related-warrants/${id}`,
      { params },
    );
  },

  getPersonRelatedTasks: (
    id: string,
    params?: IGetParams,
  ): Promise<AxiosResponse<IPaginatedListResponse<any>>> => {
    return http.get(
      `api/${ApiVersion.V1}/master-index/${MasterIndexApiPath.People}/related-tasks/${id}`,
      { params },
    );
  },

  getPersonRelatedDir: (
    id: string,
    params?: IGetParams,
  ): Promise<AxiosResponse<IPaginatedListResponse<any>>> => {
    return http.get(
      `api/${ApiVersion.V1}/master-index/${MasterIndexApiPath.People}/related-dir/${id}`,
      { params },
    );
  },

  getPersonRelatedSexOffenders: (
    id: string,
    params?: IGetParams,
  ): Promise<AxiosResponse<IPaginatedListResponse<any>>> => {
    return http.get(
      `api/${ApiVersion.V1}/master-index/${MasterIndexApiPath.People}/related-sex-offenders/${id}`,
      { params },
    );
  },

  mergeMasterPerson: (
    sourceId: string,
    body: MergeMasterPayload<'person'>,
  ): Promise<AxiosResponse<MergeMasterResponse<IMasterNamePerson>>> => {
    return http.post(
      `api/${ApiVersion.V1}/master-index/${MasterIndexApiPath.People}/${sourceId}/merge`,
      body,
    );
  },

  mergeMasterOrganization: (
    sourceId: string,
    body: MergeMasterPayload<'organization'>,
  ): Promise<AxiosResponse<MergeMasterResponse<IMasterNameOrganization>>> => {
    return http.post(
      `api/${ApiVersion.V1}/master-index/${MasterIndexApiPath.Organizations}/${sourceId}/merge`,
      body,
    );
  },

  mergeMasterVehicle: (
    sourceId: string,
    body: MergeMasterPayload<'vehicle'>,
  ): Promise<AxiosResponse<MergeMasterResponse<IMasterVehicle>>> => {
    return http.post(
      `api/${ApiVersion.V1}/master-index/${MasterIndexApiPath.Vehicles}/${sourceId}/merge`,
      body,
    );
  },

  mergeMasterAddress: (
    sourceId: string,
    body: MergeMasterPayload<'address'>,
  ): Promise<AxiosResponse<MergeMasterResponse<IMasterAddress>>> => {
    return http.post(
      `api/${ApiVersion.V1}/master-index/${MasterIndexApiPath.Addresses}/${sourceId}/merge`,
      body,
    );
  },
};
